import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"
import { TextLayer } from "pdfjs-dist"

export default class extends Controller {
  static targets = ["canvas", "currentPage", "totalPages", "prevButton", "nextButton", "zoomLevel", "pageWrapper", "textLayer", "error", "errorMessage", "previewControl"]
  static values = {
    allowedPages: Array,
    documentId: Number,
    mimeType: String,
    workerUrl: String
  }

  connect() {
    // Initialize state
    this.currentPageIndex = 0
    this.zoomLevel = 100
    this.rotation = 0
    this.pdfDoc = null
    this.imageElement = null
    this.renderTask = null
    this.userHasZoomed = false // Track if user manually zoomed

    // Get canvas and context
    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext('2d')

    // Detect if this is a PDF or image
    this.isPdf = this.mimeTypeValue === 'application/pdf'
    this.isImage = this.mimeTypeValue?.startsWith('image/')

    if (this.isPdf) {
      // Set up PDF.js worker
      pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerUrlValue
      this.loadPDF()
    } else if (this.isImage) {
      this.loadImage()
    }

    // Handle window/panel resize — recalculate fit-to-width
    this.resizeHandler = () => {
      setTimeout(async () => {
        if (!this.userHasZoomed) {
          if (this.isPdf) await this.calculateOptimalZoomForPDF()
          else if (this.isImage) this.calculateOptimalZoom()
          this.updateZoomLevel()
        }
        await this.renderCurrentPage()
        this.fitCanvasToContainer()
      }, 100)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    if (this.renderTask) {
      this.renderTask.cancel()
    }
  }

  async loadPDF() {
    try {
      const pdfUrl = `/documents/${this.documentIdValue}/pdf`

      // Load PDF using imported pdfjsLib
      const loadingTask = pdfjsLib.getDocument({ url: pdfUrl })
      this.pdfDoc = await loadingTask.promise


      // Filter pages based on allowed pages (if specified)
      this.allowedPageNumbers = this.filterAllowedPages()


      if (this.allowedPageNumbers.length === 0) {
        console.error("No allowed pages found")
        this.showError("Nepavyko įkelti PDF failo")
        return
      }

      // Set current page to first allowed page
      this.currentPageIndex = 0

      // Update navigation first
      this.updateNavigation()

      // Small delay to ensure container dimensions are available
      await new Promise(resolve => setTimeout(resolve, 50))

      // Calculate optimal zoom for first page
      await this.calculateOptimalZoomForPDF()

      // Render first page
      await this.renderCurrentPage()

      // Initial positioning for centered display
      this.fitCanvasToContainer()

    } catch (error) {
      console.error("Error loading PDF:", error)
      this.showError("Nepavyko įkelti PDF failo")
    }
  }

  async loadImage() {
    try {
      const imageUrl = `/documents/${this.documentIdValue}/image`

      // Create image element
      this.imageElement = new Image()
      this.imageElement.crossOrigin = 'anonymous'

      // Load image
      await new Promise((resolve, reject) => {
        this.imageElement.onload = resolve
        this.imageElement.onerror = reject
        this.imageElement.src = imageUrl
      })

      // For images, we only have 1 "page"
      this.allowedPageNumbers = [1]
      this.currentPageIndex = 0

      // Update navigation first
      this.updateNavigation()

      // Small delay to ensure container dimensions are available
      await new Promise(resolve => setTimeout(resolve, 50))

      // Calculate optimal initial zoom to fit container
      this.calculateOptimalZoom()

      // Render the image
      await this.renderCurrentPage()

      // Initial positioning for centered display
      this.fitCanvasToContainer()

    } catch (error) {
      console.error("Error loading image:", error)
      this.showError("Nepavyko įkelti vaizdo failo")
    }
  }

  getScrollContainer() {
    // The scrollable container is #document-viewer (parent of pageWrapper or canvas)
    const wrapper = this.hasPageWrapperTarget ? this.pageWrapperTarget : this.canvas
    return wrapper.parentElement
  }

  calculateOptimalZoom() {
    if (!this.userHasZoomed && this.imageElement) {
      // Get container dimensions
      const container = this.getScrollContainer()
      const containerWidth = container.clientWidth - 20 // Account for padding
      const containerHeight = container.clientHeight - 20

      // Get natural image dimensions
      const naturalWidth = this.imageElement.naturalWidth
      const naturalHeight = this.imageElement.naturalHeight

      // Calculate scale factors
      const scaleX = containerWidth / naturalWidth
      const scaleY = containerHeight / naturalHeight

      // For images, prefer to fit height to use vertical space
      // but ensure we don't cut off the sides
      let optimalScale = scaleY // Start with height-based scale

      // Check if this would make it too wide
      if (naturalWidth * optimalScale > containerWidth) {
        // If so, use width-based scale instead
        optimalScale = scaleX
      }

      // Convert to zoom level (percentage)
      this.zoomLevel = Math.round(optimalScale * 100)

      // Round to nearest 5% for smoother scaling
      this.zoomLevel = Math.round(this.zoomLevel / 5) * 5

      // Ensure reasonable zoom range
      this.zoomLevel = Math.max(25, Math.min(200, this.zoomLevel))
    }
  }

  async calculateOptimalZoomForPDF() {
    if (!this.userHasZoomed && this.pdfDoc && this.allowedPageNumbers.length > 0) {
      const pageNumber = this.allowedPageNumbers[0]
      const page = await this.pdfDoc.getPage(pageNumber)
      const viewport = page.getViewport({ scale: 1.0 })

      const container = this.getScrollContainer()
      const containerWidth = container.clientWidth - 20

      // Fit to width — maximizes readability, users scroll vertically
      const optimalScale = containerWidth / viewport.width

      this.zoomLevel = Math.round(optimalScale * 100)
      this.zoomLevel = Math.round(this.zoomLevel / 5) * 5
      this.zoomLevel = Math.max(25, Math.min(200, this.zoomLevel))
    }
  }

  filterAllowedPages() {
    if (!this.hasAllowedPagesValue || this.allowedPagesValue.length === 0) {
      // If no allowed pages specified, show all pages
      return Array.from({length: this.pdfDoc.numPages}, (_, i) => i + 1)
    }

    // Return the allowed page numbers (already 1-indexed)
    return this.allowedPagesValue.slice().sort((a, b) => a - b)
  }

  async renderCurrentPage() {
    if ((!this.pdfDoc && !this.imageElement) || this.allowedPageNumbers.length === 0) return

    try {
      if (this.isPdf) {
        await this.renderPDFPage()
      } else if (this.isImage) {
        await this.renderImagePage()
      }
    } catch (error) {
      if (error.name === 'RenderingCancelledException') {
        console.log("Rendering was cancelled")
      } else {
        console.error("Error rendering:", error)
      }
    }
  }

  async renderPDFPage() {
    const pageNumber = this.allowedPageNumbers[this.currentPageIndex]

    // Cancel any ongoing render task
    if (this.renderTask) {
      this.renderTask.cancel()
      this.renderTask = null
    }

    const page = await this.pdfDoc.getPage(pageNumber)

    // Calculate viewport with zoom and rotation
    const scale = this.zoomLevel / 100
    const viewport = page.getViewport({ scale, rotation: this.rotation })

    // Set canvas dimensions
    this.canvas.width = viewport.width
    this.canvas.height = viewport.height

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    // Render PDF page into canvas context
    const renderContext = {
      canvasContext: this.ctx,
      viewport: viewport
    }

    this.renderTask = page.render(renderContext)
    await this.renderTask.promise
    this.renderTask = null

    // Render text layer for text selection
    await this.renderTextLayer(page, viewport)
  }

  async renderTextLayer(page, viewport) {
    if (!this.hasTextLayerTarget) return

    // Cancel any in-flight text layer render
    if (this.activeTextLayer) {
      this.activeTextLayer.cancel()
      this.activeTextLayer = null
    }
    this.textLayerRenderGeneration = (this.textLayerRenderGeneration || 0) + 1
    const generation = this.textLayerRenderGeneration

    const textLayerDiv = this.textLayerTarget
    textLayerDiv.innerHTML = ""
    textLayerDiv.style.width = `${viewport.width}px`
    textLayerDiv.style.height = `${viewport.height}px`

    // Set CSS variables required by pdf.js TextLayer for span positioning
    const scale = this.zoomLevel / 100
    textLayerDiv.style.setProperty("--scale-factor", scale)
    textLayerDiv.style.setProperty("--total-scale-factor", scale)

    // Size the wrapper to match canvas
    if (this.hasPageWrapperTarget) {
      this.pageWrapperTarget.style.width = `${viewport.width}px`
      this.pageWrapperTarget.style.height = `${viewport.height}px`
    }

    const textContent = await page.getTextContent()
    if (generation !== this.textLayerRenderGeneration) return
    if (textContent.items.length === 0) return

    const textLayer = new TextLayer({
      textContentSource: textContent,
      container: textLayerDiv,
      viewport: viewport
    })

    this.activeTextLayer = textLayer
    await textLayer.render()
    this.activeTextLayer = null
  }

  async renderImagePage() {
    // Calculate scaled dimensions with zoom and rotation
    const scale = this.zoomLevel / 100
    let width = this.imageElement.naturalWidth * scale
    let height = this.imageElement.naturalHeight * scale

    // Handle rotation by swapping width/height
    if (this.rotation === 90 || this.rotation === 270) {
      [width, height] = [height, width]
    }

    // Set canvas dimensions
    this.canvas.width = width
    this.canvas.height = height

    // Size the wrapper to match canvas (no text layer for images)
    if (this.hasPageWrapperTarget) {
      this.pageWrapperTarget.style.width = `${width}px`
      this.pageWrapperTarget.style.height = `${height}px`
    }

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    // Save context and apply transformations
    this.ctx.save()

    // Move to center for rotation
    this.ctx.translate(width / 2, height / 2)
    this.ctx.rotate((this.rotation * Math.PI) / 180)

    // Draw image (accounting for rotation center offset)
    this.ctx.drawImage(
      this.imageElement,
      -this.imageElement.naturalWidth * scale / 2,
      -this.imageElement.naturalHeight * scale / 2,
      this.imageElement.naturalWidth * scale,
      this.imageElement.naturalHeight * scale
    )

    // Restore context
    this.ctx.restore()
  }

  fitCanvasToContainer() {
    // The container is the scrollable #document-viewer div
    const wrapper = this.hasPageWrapperTarget ? this.pageWrapperTarget : this.canvas
    const container = wrapper.parentElement
    if (!container) return

    // Simple centering: use text-align for horizontal, margin for vertical
    container.style.textAlign = 'center'
    container.style.overflow = 'auto'

    wrapper.style.display = 'inline-block'
    wrapper.style.verticalAlign = 'middle'

    // Center vertically when canvas fits in container
    const containerHeight = container.clientHeight
    const canvasHeight = this.canvas.offsetHeight || this.canvas.height

    if (canvasHeight <= containerHeight) {
      const topMargin = Math.max(0, (containerHeight - canvasHeight) / 2)
      wrapper.style.marginTop = `${topMargin}px`
    } else {
      wrapper.style.marginTop = '0'
    }
  }

  nextPage() {
    if (this.currentPageIndex < this.allowedPageNumbers.length - 1) {
      this.currentPageIndex++
      this.updateNavigation()
      this.renderCurrentPage().then(() => {
        // Center the new page if user hasn't manually zoomed
        if (!this.userHasZoomed) {
          this.fitCanvasToContainer()
        }
      })
    }
  }

  previousPage() {
    if (this.currentPageIndex > 0) {
      this.currentPageIndex--
      this.updateNavigation()
      this.renderCurrentPage().then(() => {
        // Center the new page if user hasn't manually zoomed
        if (!this.userHasZoomed) {
          this.fitCanvasToContainer()
        }
      })
    }
  }

  zoomIn() {
    if (this.zoomLevel < 200) {
      this.userHasZoomed = true

      // Save current scroll position and viewport center
      const container = this.getScrollContainer()
      const oldScrollLeft = container.scrollLeft
      const oldScrollTop = container.scrollTop
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight

      // Calculate the center point relative to the current canvas content
      const oldCanvasWidth = this.canvas.offsetWidth
      const oldCanvasHeight = this.canvas.offsetHeight

      // Find the point that's currently at the center of the viewport
      const centerX = oldScrollLeft + containerWidth / 2
      const centerY = oldScrollTop + containerHeight / 2

      // Convert to relative coordinates (0 to 1)
      const relativeCenterX = centerX / oldCanvasWidth
      const relativeCenterY = centerY / oldCanvasHeight

      this.zoomLevel += 25
      this.updateZoomLevel()
      this.renderCurrentPage().then(() => {
        this.fitCanvasToContainer()

        // Calculate new dimensions and where the center point should be
        const newCanvasWidth = this.canvas.offsetWidth
        const newCanvasHeight = this.canvas.offsetHeight

        // Scale the center point to the new canvas size
        const newCenterX = relativeCenterX * newCanvasWidth
        const newCenterY = relativeCenterY * newCanvasHeight

        // Calculate new scroll position to keep the same point centered
        const newScrollLeft = newCenterX - containerWidth / 2
        const newScrollTop = newCenterY - containerHeight / 2

        container.scrollLeft = Math.max(0, newScrollLeft)
        container.scrollTop = Math.max(0, newScrollTop)
      })
    }
  }

  zoomOut() {
    if (this.zoomLevel > 25) {
      this.userHasZoomed = true

      // Save current scroll position and viewport center
      const container = this.getScrollContainer()
      const oldScrollLeft = container.scrollLeft
      const oldScrollTop = container.scrollTop
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight

      // Calculate the center point relative to the current canvas content
      const oldCanvasWidth = this.canvas.offsetWidth
      const oldCanvasHeight = this.canvas.offsetHeight

      // Find the point that's currently at the center of the viewport
      const centerX = oldScrollLeft + containerWidth / 2
      const centerY = oldScrollTop + containerHeight / 2

      // Convert to relative coordinates (0 to 1)
      const relativeCenterX = centerX / oldCanvasWidth
      const relativeCenterY = centerY / oldCanvasHeight

      this.zoomLevel -= 25
      this.updateZoomLevel()
      this.renderCurrentPage().then(() => {
        this.fitCanvasToContainer()

        // Calculate new dimensions and where the center point should be
        const newCanvasWidth = this.canvas.offsetWidth
        const newCanvasHeight = this.canvas.offsetHeight

        // Scale the center point to the new canvas size
        const newCenterX = relativeCenterX * newCanvasWidth
        const newCenterY = relativeCenterY * newCanvasHeight

        // Calculate new scroll position to keep the same point centered
        const newScrollLeft = newCenterX - containerWidth / 2
        const newScrollTop = newCenterY - containerHeight / 2

        container.scrollLeft = Math.max(0, newScrollLeft)
        container.scrollTop = Math.max(0, newScrollTop)
      })
    }
  }

  rotateLeft() {
    this.rotation = (this.rotation - 90) % 360
    this.renderCurrentPage()
  }

  rotateRight() {
    this.rotation = (this.rotation + 90) % 360
    this.renderCurrentPage()
  }

  updateNavigation() {
    if (!this.allowedPageNumbers || this.allowedPageNumbers.length === 0) return

    // Display current page number (1-indexed in UI)
    this.currentPageTarget.textContent = (this.currentPageIndex + 1).toString()
    this.totalPagesTarget.textContent = this.allowedPageNumbers.length.toString()

    // Update button states
    this.prevButtonTarget.disabled = this.currentPageIndex === 0
    this.nextButtonTarget.disabled = this.currentPageIndex === this.allowedPageNumbers.length - 1
  }

  updateZoomLevel() {
    this.zoomLevelTarget.textContent = `${this.zoomLevel}%`
  }

  showError(message) {
    this.pageWrapperTarget.classList.add('hidden')
    this.previewControlTargets.forEach(control => { control.disabled = true })
    this.errorMessageTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }
}
